import { Observable } from 'rxjs';
import { Base64File, PayloadApiResponse } from './types.domain';

/**
 * Read model for the FrankieOne Trust Analyzer (GLB-Trust-Analysis), which extracts a trust's
 * structure from its deed. Shaped to mirror the KYB read model (kyb-entity-summary.domain) so the
 * KYC tab renders both the same way.
 *
 * The deed itself lives in Azure Blob Storage; FrankieOne holds its own copy as a TRUST_DEED
 * document. Both references are kept on the run so the portal can show the file it was given
 * without depending on FrankieOne being reachable.
 */

// Mirrors ApplicationTrustAnalysisRun.status.
export type TrustAnalysisRunStatus =
  | 'UPLOADING'
  | 'UPLOAD_FAILED'
  | 'INITIATING'
  | 'RETRY_PENDING'
  | 'IN_PROGRESS'
  | 'CALLBACK_RECEIVED'
  | 'RESULT_RETRY_PENDING'
  | 'COMPLETED'
  | 'INITIATION_FAILED'
  | 'ANALYSIS_FAILED'
  | 'RESULT_PROCESSING_FAILED';

/**
 * FrankieOne's per-analysis status. VALID is the current analysis for the deed; STALE means the
 * deed's attachment changed after the analysis ran; ERROR means extraction did not complete.
 *
 * Note this is not the PROCESSING/FAILED/COMPLETE/CONFIRMED set in FrankieOne's implementation
 * guide — that set is from an older experimental spec and the live API does not return it.
 * Confirmation is a separate boolean, not a status.
 */
export type TrustAnalysisStatus = 'VALID' | 'STALE' | 'ERROR';

export type TrustAnalysisTrustType = 'DISCRETIONARY' | 'UNIT' | 'SELF_MANAGED_SUPER_FUND' | 'UNKNOWN';

/** UI-facing collapse of TrustAnalysisRunStatus, matching kybDisplayStatus. */
export type TrustAnalysisDisplayStatus = 'pending' | 'resolved' | 'failed';

/**
 * A party's role in the trust. The core roles (trustee, settlor) are returned for every trust;
 * the rest come from the type-specific block and depend on the detected trust type.
 */
export type TrustAnalysisPartyRole =
  | 'TRUSTEE'
  | 'SETTLOR'
  | 'APPOINTOR'
  | 'PROTECTOR'
  | 'BENEFICIARY'
  | 'GENERAL_BENEFICIARY'
  | 'UNIT_HOLDER'
  | 'MEMBER'
  | 'OTHER';

/**
 * TEXT covers a party the deed only describes in words ("the children of the settlor"), which has
 * no entry in FrankieOne's linked* maps and so cannot be screened as a person or company.
 */
export type TrustAnalysisPartyType = 'INDIVIDUAL' | 'ORGANIZATION' | 'UNKNOWN' | 'TEXT';

export interface TrustAnalysisParty {
  id: number;
  role: TrustAnalysisPartyRole;
  partyType: TrustAnalysisPartyType;
  /** FrankieOne's key into the linked* maps; null for a TEXT party. */
  frankieOneEntityId: string | null;
  displayName: string | null;
  givenName: string | null;
  middleName: string | null;
  familyName: string | null;
  dateOfBirth: string | null;
  organizationName: string | null;
  abn: string | null;
  acn: string | null;
  unstructuredAddress: string | null;
  unitsHeld: number | null;
  unitPercentage: number | null;
  /** Deed page numbers the value was read from, so a reviewer can check it against the source. */
  sourcePages: number[];
}

export interface TrustAnalysisResultDetails {
  frankieOneAnalysisId: string | null;
  analysisStatus: TrustAnalysisStatus | null;
  /** Whether a reviewer has signed the extracted structure off with FrankieOne. */
  isConfirmed: boolean;
  trustName: string | null;
  detectedTrustType: TrustAnalysisTrustType | null;
  providedTrustType: TrustAnalysisTrustType | null;
  establishmentDate: string | null;
  establishmentCountry: string | null;
  establishmentSubdivision: string | null;
  certificationDate: string | null;
  executionDate: string | null;
  totalUnitsOnIssue: number | null;
  /** Why the analyzer could not classify the trust; only set when the type is UNKNOWN. */
  unknownTypeReasons: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  parties: TrustAnalysisParty[];
}

/** One trust analysis run against one deed, with its result when there is one. */
export interface TrustAnalysis {
  runId: number;
  commercialEntityId: number;
  status: TrustAnalysisRunStatus;
  fileName: string;
  /** Null while the upload is still in flight or if it failed. */
  blobPath: string | null;
  fileSizeBytes: number | null;
  /** Populated once FrankieOne has accepted the deed as a TRUST_DEED document. */
  frankieOneDocumentId: string | null;
  /** The Documents tab condition the deed came from; null for deeds predating per-entity boxes. */
  conditionValue: string | null;
  lastError: string | null;
  createdAt: string;
  completedAt: string | null;
  /** Null until the workflow returns; present but with an errorCode when extraction failed. */
  result: TrustAnalysisResultDetails | null;
}

export interface TrustAnalysisSummaryResult {
  applicationId: number;
  /** Every run on the application, newest first within each entity; entities with none are absent. */
  analyses: TrustAnalysis[];
}

export interface UploadTrustDeedInput {
  applicationId: number;
  commercialEntityId: number;
  file: Base64File;
  /** The trust deed condition being uploaded into; omitted only by the legacy upload path. */
  conditionValue?: string;
}

/** Payload for delayed GLB-Trust-Analysis execution retries after the deed is stored. */
export type TrustAnalysisInitiateJobPayload = {
  applicationId: number;
  applicationTrustAnalysisRunId: number;
};

/** Payload for delayed analysis-result retrieval retries after WorkflowComplete. */
export type TrustAnalysisResultJobPayload = {
  applicationId: number;
  applicationTrustAnalysisRunId: number;
};

export type TrustAnalysisSummaryFn = (applicationId: number) => Observable<TrustAnalysisSummaryResult>;
export type UploadTrustDeedFn = (input: UploadTrustDeedInput) => Observable<PayloadApiResponse<TrustAnalysis>>;
export type RetryTrustAnalysisFn = (runId: number) => Observable<PayloadApiResponse<TrustAnalysis>>;

export function trustAnalysisDisplayStatus(status: TrustAnalysisRunStatus): TrustAnalysisDisplayStatus {
  switch (status) {
    case 'COMPLETED':
      return 'resolved';
    case 'UPLOAD_FAILED':
    case 'INITIATION_FAILED':
    case 'ANALYSIS_FAILED':
    case 'RESULT_PROCESSING_FAILED':
      return 'failed';
    default:
      return 'pending';
  }
}

export function trustAnalysisStatusLabel(status: TrustAnalysisRunStatus): string {
  switch (status) {
    case 'UPLOADING':
      return 'Uploading deed';
    case 'UPLOAD_FAILED':
      return 'Upload failed';
    case 'INITIATING':
    case 'RETRY_PENDING':
      return 'Starting analysis';
    case 'IN_PROGRESS':
    case 'CALLBACK_RECEIVED':
    case 'RESULT_RETRY_PENDING':
      return 'Analysis in progress';
    case 'COMPLETED':
      return 'Analysis complete';
    case 'INITIATION_FAILED':
      return 'Could not start analysis';
    case 'ANALYSIS_FAILED':
      return 'Analysis failed';
    case 'RESULT_PROCESSING_FAILED':
      return 'Could not read analysis result';
  }
}

export function trustAnalysisTrustTypeLabel(type: TrustAnalysisTrustType | null): string {
  switch (type) {
    case 'DISCRETIONARY':
      return 'Discretionary (family) trust';
    case 'UNIT':
      return 'Unit trust';
    case 'SELF_MANAGED_SUPER_FUND':
      return 'Self-managed super fund';
    case 'UNKNOWN':
      return 'Could not be classified';
    default:
      return '—';
  }
}

export function trustAnalysisPartyRoleLabel(role: TrustAnalysisPartyRole): string {
  switch (role) {
    case 'TRUSTEE':
      return 'Trustee';
    case 'SETTLOR':
      return 'Settlor';
    case 'APPOINTOR':
      return 'Appointor';
    case 'PROTECTOR':
      return 'Protector';
    case 'BENEFICIARY':
      return 'Beneficiary';
    case 'GENERAL_BENEFICIARY':
      return 'General beneficiary';
    case 'UNIT_HOLDER':
      return 'Unit holder';
    case 'MEMBER':
      return 'Member';
    case 'OTHER':
      return 'Other';
  }
}

/**
 * The analyzer returns a display name for a company but only given/family names for a person, and
 * neither for a party the deed describes in words, so the name has to be assembled per party type.
 */
export function trustAnalysisPartyName(party: TrustAnalysisParty): string {
  if (party.organizationName) {
    return party.organizationName;
  }
  const parts = [party.givenName, party.middleName, party.familyName].filter(p => !!p);
  if (parts.length) {
    return parts.join(' ');
  }
  return party.displayName ?? 'Unnamed party';
}

/** True once there is something for a reviewer to read, whether it succeeded or not. */
export function trustAnalysisHasResult(analysis: TrustAnalysis): boolean {
  return analysis.result !== null;
}

/** Parties grouped by role, in the order a reviewer reads a deed, skipping roles with no parties. */
export function trustAnalysisPartiesByRole(
  parties: TrustAnalysisParty[],
): { role: TrustAnalysisPartyRole; label: string; parties: TrustAnalysisParty[] }[] {
  const order: TrustAnalysisPartyRole[] = [
    'TRUSTEE',
    'APPOINTOR',
    'SETTLOR',
    'PROTECTOR',
    'BENEFICIARY',
    'GENERAL_BENEFICIARY',
    'UNIT_HOLDER',
    'MEMBER',
    'OTHER',
  ];
  return order
    .map(role => ({
      role,
      label: trustAnalysisPartyRoleLabel(role),
      parties: parties.filter(p => p.role === role),
    }))
    .filter(group => group.parties.length > 0);
}
